import axios from "axios";
import { generateRequestId } from "../http/requestId";
import { adaptFilesResponse } from "./adapters";
import {
  AnalyticsActionsResponse,
  AnalyticsBranchesResponse,
  AnalyticsSummaryResponse,
  AnalyticsTimeSeriesResponse,
  AnalyticsTranslationHealthResponse,
  AnalyticsWorkspacesResponse,
  BranchesResponse,
  BindingsPreviewResponse,
  DiffResponse,
  EditorDataResponse,
  LanguagesCatalogResponse,
  LocalizationMatrixResponse,
  ObservabilityResponse,
  OperationRun,
  OperationStartResponse,
  RepositoryGroup,
  RepositoryGroupsResponse,
  RepositoryFileSearchResponse,
  RepositoryTreeResponse,
  PublishResponse,
  SaveBindingsResponse,
  SaveEditorResponse,
  SyncKeysResponse,
  ValidationResponse,
  WorkspaceConfig,
  WorkspaceListResponse,
  WorkspaceStateResponse,
  WorkspaceSummary,
  MergePreviewResponse,
} from "./types";

const withRequestHeaders = () => ({
  withCredentials: true,
  headers: {
    "x-request-id": generateRequestId(),
  },
});

const inflightRequests = new Map<string, Promise<unknown>>();
const responseCache = new Map<string, { expiresAt: number; data: unknown }>();

const getCachedKey = (key: string, params?: Record<string, unknown>) =>
  `${key}:${JSON.stringify(params || {})}`;

type AnalyticsBaseParams = {
  workspaceKey?: string;
  from?: string;
  to?: string;
};

const buildAnalyticsParams = (params?: AnalyticsBaseParams) => ({
  workspaceKey: params?.workspaceKey || undefined,
  from: params?.from || undefined,
  to: params?.to || undefined,
});

const dedupeGet = async <T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs = 0
): Promise<T> => {
  const now = Date.now();
  const cached = responseCache.get(key);
  if (cached && cached.expiresAt > now) {
    return cached.data as T;
  }

  const inflight = inflightRequests.get(key);
  if (inflight) {
    return inflight as Promise<T>;
  }

  const request = fetcher()
    .then((data) => {
      if (ttlMs > 0) {
        responseCache.set(key, { expiresAt: now + ttlMs, data });
      }
      return data;
    })
    .finally(() => {
      inflightRequests.delete(key);
    });

  inflightRequests.set(key, request as Promise<unknown>);
  return request;
};

export const localizationClient = {
  getCatalogLanguages() {
    const cacheKey = getCachedKey("catalog/languages");
    return dedupeGet(
      cacheKey,
      () =>
        axios
          .get<LanguagesCatalogResponse>(`/api/v2/catalog/languages`, withRequestHeaders())
          .then((res) => res.data),
      10 * 60 * 1000
    );
  },

  getRepositoryGroups(owner: string, repo: string) {
    return axios
      .get<RepositoryGroupsResponse>(
        `/api/v2/repositories/${owner}/${repo}/groups`,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  createRepositoryGroup(
    owner: string,
    repo: string,
    payload: Pick<RepositoryGroup, "groupKey" | "groupName"> & { description?: string }
  ) {
    return axios
      .post<RepositoryGroup>(
        `/api/v2/repositories/${owner}/${repo}/groups`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  patchRepositoryGroup(
    owner: string,
    repo: string,
    groupKey: string,
    payload: Pick<RepositoryGroup, "groupName"> & { description?: string }
  ) {
    return axios
      .patch<RepositoryGroup>(
        `/api/v2/repositories/${owner}/${repo}/groups/${groupKey}`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  deleteRepositoryGroup(owner: string, repo: string, groupKey: string) {
    return axios.delete(
      `/api/v2/repositories/${owner}/${repo}/groups/${groupKey}`,
      withRequestHeaders()
    );
  },

  getWorkspaces(owner: string, repo: string) {
    return axios
      .get<WorkspaceListResponse>(
        `/api/v2/repositories/${owner}/${repo}/workspaces`,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  createWorkspace(
    owner: string,
    repo: string,
    payload: Pick<WorkspaceSummary, "workspaceKey" | "workspaceName"> & {
      description?: string;
    }
  ) {
    return axios
      .post<WorkspaceConfig>(
        `/api/v2/repositories/${owner}/${repo}/workspaces`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  patchWorkspace(
    owner: string,
    repo: string,
    workspaceKey: string,
    payload: Pick<WorkspaceSummary, "workspaceName"> & { description?: string }
  ) {
    return axios
      .patch<WorkspaceConfig>(
        `/api/v2/repositories/${owner}/${repo}/workspaces/${workspaceKey}`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  deleteWorkspace(owner: string, repo: string, workspaceKey: string) {
    return axios.delete(
      `/api/v2/repositories/${owner}/${repo}/workspaces/${workspaceKey}`,
      withRequestHeaders()
    );
  },

  getWorkspace(owner: string, repo: string, workspaceKey?: string) {
    return axios
      .get<WorkspaceConfig>(
        `/api/v2/repositories/${owner}/${repo}/workspace`,
        {
          ...withRequestHeaders(),
          params: workspaceKey ? { workspaceKey } : undefined,
        }
      )
      .then((res) => res.data);
  },

  updateWorkspace(
    owner: string,
    repo: string,
    payload: Partial<WorkspaceConfig> & { workspaceKey?: string }
  ) {
    return axios
      .put<WorkspaceConfig>(
        `/api/v2/repositories/${owner}/${repo}/workspace`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  updateWorkspaceBaseLanguage(
    owner: string,
    repo: string,
    payload: { workspaceKey: string; baseLanguage: string }
  ) {
    return axios
      .patch<WorkspaceConfig>(
        `/api/v2/repositories/${owner}/${repo}/workspace/base-language`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  discoverWorkspace(owner: string, repo: string, workspaceKey?: string) {
    return axios
      .post<WorkspaceConfig>(
        `/api/v2/repositories/${owner}/${repo}/workspace/discover`,
        workspaceKey ? { workspaceKey } : {},
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  getWorkspaceState(owner: string, repo: string, workspaceKey: string, ref?: string) {
    const params = { owner, repo, workspaceKey, ref: ref || "" };
    const cacheKey = getCachedKey("workspace/state", params);
    return dedupeGet(
      cacheKey,
      () =>
        axios
          .get<WorkspaceStateResponse>(
            `/api/v2/repositories/${owner}/${repo}/workspace/state`,
            {
              ...withRequestHeaders(),
              params: { workspaceKey, ref },
            }
          )
          .then((res) => res.data),
      1500
    );
  },

  getRepositoryTree(
    owner: string,
    repo: string,
    params: {
      workspaceKey: string;
      ref?: string;
      path?: string;
      onlyJson?: boolean;
      search?: string;
      limit?: number;
      cursor?: number;
      refresh?: boolean;
    }
  ) {
    return axios
      .get<RepositoryTreeResponse>(`/api/v2/repositories/${owner}/${repo}/tree`, {
        ...withRequestHeaders(),
        params,
      })
      .then((res) => res.data);
  },

  searchRepositoryFiles(
    owner: string,
    repo: string,
    params: {
      workspaceKey: string;
      ref?: string;
      search?: string;
      limit?: number;
      cursor?: number;
    }
  ) {
    return axios
      .get<RepositoryFileSearchResponse>(`/api/v2/repositories/${owner}/${repo}/files`, {
        ...withRequestHeaders(),
        params,
      })
      .then((res) => adaptFilesResponse(res.data as never, params.cursor || 0));
  },

  previewBindings(
    owner: string,
    repo: string,
    workspaceKey: string,
    bindings: WorkspaceConfig["languageBindings"]
  ) {
    return axios
      .post<BindingsPreviewResponse>(
        `/api/v2/repositories/${owner}/${repo}/workspace/bindings/preview`,
        { workspaceKey, bindings },
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  saveBindings(
    owner: string,
    repo: string,
    payload: {
      workspaceKey: string;
      mode: "replace" | "merge";
      bindings: WorkspaceConfig["languageBindings"];
    }
  ) {
    return axios
      .put<SaveBindingsResponse>(
        `/api/v2/repositories/${owner}/${repo}/workspace/bindings`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  getEditorData(
    owner: string,
    repo: string,
    params: { workspaceKey: string; group?: string; branch?: string; autoMerge?: boolean }
  ) {
    const cacheKey = getCachedKey("editor-data", { owner, repo, ...params });
    return dedupeGet(cacheKey, () =>
      axios
        .get<EditorDataResponse>(`/api/v2/localization/${owner}/${repo}/editor-data`, {
          ...withRequestHeaders(),
          params,
        })
        .then((res) => res.data)
    );
  },

  getWorkspaceBranches(owner: string, repo: string, workspaceKey: string) {
    const cacheKey = getCachedKey("branches", { owner, repo, workspaceKey });
    return dedupeGet(
      cacheKey,
      () =>
        axios
          .get<BranchesResponse>(`/api/v2/localization/${owner}/${repo}/branches`, {
            ...withRequestHeaders(),
            params: { workspaceKey },
          })
          .then((res) => res.data),
      30 * 1000
    );
  },

  previewMergeImpact(
    owner: string,
    repo: string,
    payload: { workspaceKey: string; branch?: string; group?: string }
  ) {
    return axios
      .post<MergePreviewResponse>(
        `/api/v2/localization/${owner}/${repo}/merge-preview`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  syncKeys(
    owner: string,
    repo: string,
    payload: { workspaceKey: string; branch?: string; group?: string; commitMessage?: string }
  ) {
    return axios
      .post<SyncKeysResponse>(
        `/api/v2/localization/${owner}/${repo}/sync-keys`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  switchWorkspaceBranch(
    owner: string,
    repo: string,
    payload: {
      workspaceKey: string;
      branch: string;
      createIfMissing?: boolean;
      fromBranch?: string;
    }
  ) {
    return axios
      .post<WorkspaceConfig>(
        `/api/v2/localization/${owner}/${repo}/branches/switch`,
        payload,
        withRequestHeaders()
      )
      .then((res) => {
        const prefix = `branches:${JSON.stringify({ owner, repo, workspaceKey: payload.workspaceKey })}`;
        responseCache.delete(prefix);
        return res.data;
      });
  },

  createWorkspaceBranch(
    owner: string,
    repo: string,
    payload: {
      workspaceKey: string;
      branch: string;
      fromBranch?: string;
    }
  ) {
    return axios
      .post<WorkspaceConfig>(
        `/api/v2/localization/${owner}/${repo}/branches/create`,
        payload,
        withRequestHeaders()
      )
      .then((res) => {
        const prefix = `branches:${JSON.stringify({ owner, repo, workspaceKey: payload.workspaceKey })}`;
        responseCache.delete(prefix);
        return res.data;
      });
  },

  saveEditorData(
    owner: string,
    repo: string,
    payload: {
      workspaceKey: string;
      commitMessage: string;
      locales: Array<{ filePath: string; content: Record<string, unknown> }>;
    }
  ) {
    return axios
      .post<SaveEditorResponse>(
        `/api/v2/localization/${owner}/${repo}/save`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  publishWorkspace(
    owner: string,
    repo: string,
    payload: { workspaceKey: string; title?: string; body?: string }
  ) {
    return axios
      .post<PublishResponse>(
        `/api/v2/localization/${owner}/${repo}/publish`,
        payload,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  getMatrix(owner: string, repo: string, workspaceKey?: string) {
    return axios
      .get<LocalizationMatrixResponse>(
        `/api/v2/localization/${owner}/${repo}/matrix`,
        {
          ...withRequestHeaders(),
          params: workspaceKey ? { workspaceKey } : undefined,
        }
      )
      .then((res) => res.data);
  },

  validateWorkspace(owner: string, repo: string, workspaceKey?: string) {
    return axios
      .post<ValidationResponse>(
        `/api/v2/localization/${owner}/${repo}/validate`,
        workspaceKey ? { workspaceKey } : {},
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  getDiff(owner: string, repo: string, workspaceKey?: string) {
    return axios
      .get<DiffResponse>(`/api/v2/localization/${owner}/${repo}/diff`, {
        ...withRequestHeaders(),
        params: workspaceKey ? { workspaceKey } : undefined,
      })
      .then((res) => res.data);
  },

  startOperation(
    owner: string,
    repo: string,
    action: string,
    payload: Record<string, unknown> = {},
    workspaceKey?: string
  ) {
    return axios
      .post<OperationStartResponse>(
        `/api/v2/localization/${owner}/${repo}/operations`,
        { action, payload, workspaceKey },
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  getOperationStatus(owner: string, repo: string, operationId: string) {
    return axios
      .get<OperationRun>(
        `/api/v2/localization/${owner}/${repo}/operations/${operationId}`,
        withRequestHeaders()
      )
      .then((res) => res.data);
  },

  getObservabilityLogs(owner: string, repo: string) {
    return axios
      .get<ObservabilityResponse>(`/api/v2/observability/logs`, {
        ...withRequestHeaders(),
        params: { owner, repo },
      })
      .then((res) => res.data);
  },

  getAnalyticsSummary(owner: string, repo: string, params?: AnalyticsBaseParams) {
    return axios
      .get<AnalyticsSummaryResponse>(`/api/v2/analytics/${owner}/${repo}/summary`, {
        ...withRequestHeaders(),
        params: buildAnalyticsParams(params),
      })
      .then((res) => res.data);
  },

  getAnalyticsTimeSeries(
    owner: string,
    repo: string,
    params: AnalyticsBaseParams & { interval: "hour" | "day" | "week"; metric: "events" | "save" | "publish" | "failures" }
  ) {
    return axios
      .get<AnalyticsTimeSeriesResponse>(`/api/v2/analytics/${owner}/${repo}/timeseries`, {
        ...withRequestHeaders(),
        params: {
          ...buildAnalyticsParams(params),
          interval: params.interval,
          metric: params.metric,
        },
      })
      .then((res) => res.data);
  },

  getAnalyticsActions(owner: string, repo: string, params?: AnalyticsBaseParams) {
    return axios
      .get<AnalyticsActionsResponse>(`/api/v2/analytics/${owner}/${repo}/actions`, {
        ...withRequestHeaders(),
        params: buildAnalyticsParams(params),
      })
      .then((res) => res.data);
  },

  getAnalyticsWorkspaces(owner: string, repo: string, params?: AnalyticsBaseParams) {
    return axios
      .get<AnalyticsWorkspacesResponse>(`/api/v2/analytics/${owner}/${repo}/workspaces`, {
        ...withRequestHeaders(),
        params: buildAnalyticsParams(params),
      })
      .then((res) => res.data);
  },

  getAnalyticsTranslationHealth(owner: string, repo: string, params: AnalyticsBaseParams & { workspaceKey: string }) {
    return axios
      .get<AnalyticsTranslationHealthResponse>(
        `/api/v2/analytics/${owner}/${repo}/translation-health`,
        {
          ...withRequestHeaders(),
          params: buildAnalyticsParams(params),
        }
      )
      .then((res) => res.data);
  },

  getAnalyticsBranches(owner: string, repo: string, params?: AnalyticsBaseParams) {
    return axios
      .get<AnalyticsBranchesResponse>(`/api/v2/analytics/${owner}/${repo}/branches`, {
        ...withRequestHeaders(),
        params: buildAnalyticsParams(params),
      })
      .then((res) => res.data);
  },
};
